import type { RowData, Table } from "@tanstack/react-table";
import { useMemo } from "react";

declare module "@tanstack/react-table" {
	interface ColumnMeta<TData extends RowData, TValue> {
		/** Stretch column: takes remaining space (`minmax(0, 1fr)` in the grid template). Size is ignored visually. */
		stretch?: boolean;
		/** className applied to <th>/<td> for this column. */
		cellClassName?: string;
		/** className applied only to <th>. */
		headerClassName?: string;
		/** Human label shown in the column-visibility picker. */
		label?: string;
	}
}

/**
 * Build `--col-{id}-size` CSS variables for the table root.
 * Stretch columns resolve to `minmax(0, 1fr)`; others resolve to `{size}px`.
 *
 * Subscribes to `columnSizing` (committed widths) AND `columnSizingInfo` (live drag),
 * so `columnResizeMode: "onChange"` produces fluid drag without re-rendering body cells —
 * cells read their width from the CSS variable, not from React props.
 */
export function useColumnSizeVars<TData>(
	table: Table<TData>,
): Record<string, string> {
	const columnSizing = table.getState().columnSizing;
	const columnSizingInfo = table.getState().columnSizingInfo;
	const columnVisibility = table.getState().columnVisibility;

	// biome-ignore lint/correctness/useExhaustiveDependencies: deps are state subscriptions, not values referenced inside the memo body
	return useMemo(() => {
		const vars: Record<string, string> = {};
		for (const header of table.getFlatHeaders()) {
			const stretch = header.column.columnDef.meta?.stretch;
			vars[`--col-${header.column.id}-size`] = stretch
				? "minmax(0, 1fr)"
				: `${header.getSize()}px`;
		}
		return vars;
	}, [table, columnSizing, columnSizingInfo, columnVisibility]);
}

/**
 * grid-template-columns string referencing the CSS vars above.
 * Recomputed only when the set of visible columns changes.
 */
export function useGridTemplateColumns<TData>(table: Table<TData>): string {
	const columnVisibility = table.getState().columnVisibility;
	// biome-ignore lint/correctness/useExhaustiveDependencies: columnVisibility is a state subscription, not directly read in the body
	return useMemo(() => {
		return table
			.getVisibleLeafColumns()
			.map((col) => `var(--col-${col.id}-size)`)
			.join(" ");
	}, [table, columnVisibility]);
}
